import React, { Component } from "react";
import axios from "axios";
import { Row, Col, Form, Button, Card, Alert, Table } from "react-bootstrap";
import CartContext from "./CartContext";
import Select from "react-select";

export default class LiveFishMovement extends Component {
  static contextType = CartContext;

  state = {
    products: [],
    units: [],

    filterFromDate: new Date().toISOString().substring(0, 10), // today
    filterToDate: new Date().toISOString().substring(0, 10), // today
    filterType: "All",

    // 🔑 GLOBAL lookups (for display)
    allTanks: [],
    allPartitions: [],

    // 🔑 DESTINATION lookups
    toTanks: [],
    toPartitions: [],

    movements: [],

    selectedProduct: null,
    sourceQty: 0,

    form: {
      productId: "",
      fromUnitId: "",
      fromTankId: "",
      fromPartitionId: "",
      toUnitId: "",
      toTankId: "",
      toPartitionId: "",
      quantity: "",
      movementType: "Transfer",
      remarks: "",
    },

    error: "",
    success: "",
  };

  getProductOptions = () => {
    const { products } = this.state;

    return products.map((p) => {
      const unit = this.getUnitName(p.storeUnitId);
      const tank = this.getTankName(p.tankId);
      const part = this.getPartitionName(p.partitionId);

      return {
        value: p.id,
        label: `${p.name} | ${unit} | ${tank} | ${part} | InStk: ${p.inStockQty ?? 0}`,
        searchText: `${p.name} ${unit} ${tank} ${part}`.toLowerCase(),
        product: p,
      };
    });
  };

  async componentDidMount() {
    this.setState({ products: this.context.prods });
    await this.loadUnits();
    await this.loadAllTanks();
    await this.loadAllPartitions();
    this.loadMovements();
  }

  /* ================= GLOBAL LOADERS ================= */

  loadUnits = async () => {
    const res = await axios.get(process.env.REACT_APP_API + "StorageUnit", {
      params: { storeId: this.context.storeId },
    });
    this.setState({ units: res.data });
  };

  loadAllTanks = async () => {
    const res = await axios.get(process.env.REACT_APP_API + "Tank/all", {
      params: { storeId: this.context.storeId },
    });
    this.setState({ allTanks: res.data });
  };

  loadAllPartitions = async () => {
    const res = await axios.get(
      process.env.REACT_APP_API + "TankPartition/all",
      { params: { storeId: this.context.storeId } },
    );
    this.setState({ allPartitions: res.data });
  };

  /* ================= DESTINATION LOADERS ================= */

  loadToTanks = async (unitId) => {
    if (!unitId) return;
    const res = await axios.get(process.env.REACT_APP_API + "Tank", {
      params: { storeId: this.context.storeId, storageUnitId: unitId },
    });
    this.setState({
      toTanks: res.data,
      toPartitions: [],
      form: { ...this.state.form, toTankId: "", toPartitionId: "" },
    });
  };

  loadToPartitions = async (tankId) => {
    if (!tankId) return;
    const res = await axios.get(process.env.REACT_APP_API + "TankPartition", {
      params: { storeId: this.context.storeId, tankId },
    });
    this.setState({
      toPartitions: res.data,
      form: { ...this.state.form, toPartitionId: "" },
    });
  };

  /* ================= HANDLERS ================= */

  onProductChange = (e) => {
    const product = this.state.products.find((p) => p.id === e.target.value);
    if (!product) return;

    this.setState({
      selectedProduct: product,
      sourceQty: product.inStockQty ?? 0,
      form: {
        ...this.state.form,
        productId: product.id,
        fromUnitId: product.storeUnitId || "",
        fromTankId: product.tankId || "",
        fromPartitionId: product.partitionId || "",
      },
    });
  };

  handleChange = (e) => {
    const { name, value } = e.target;
    this.setState({ form: { ...this.state.form, [name]: value } });
  };

  saveMovement = async () => {
    const { form, sourceQty } = this.state;

    if (!form.productId || !form.quantity)
      return this.setState({ error: "Product & Quantity required" });

    if (Number(form.quantity) > sourceQty)
      return this.setState({ error: "Quantity exceeds stock" });

    try {
      await axios.post(process.env.REACT_APP_API + "LiveFishMovement", {
        ...form,
        quantity: Number(form.quantity),
      });

      this.setState({
        success: "Movement saved",
        error: "",
        selectedProduct: null,
        sourceQty: 0,
        toTanks: [],
        toPartitions: [],
        form: {
          productId: "",
          fromUnitId: "",
          fromTankId: "",
          fromPartitionId: "",
          toUnitId: "",
          toTankId: "",
          toPartitionId: "",
          quantity: "",
          movementType: "Transfer",
          remarks: "",
        },
      });

      this.loadMovements();
    } catch (err) {
      this.setState({ error: err.response?.data || "Save failed" });
    }
  };

  /* ================= NAME HELPERS (GLOBAL) ================= */

  getUnitName = (id) =>
    this.state.units.find((u) => u.id === id)?.unitName || "-";

  getTankName = (id) =>
    this.state.allTanks.find((t) => t.id === id)?.tankCode || "-";

  getPartitionName = (id) =>
    this.state.allPartitions.find((p) => p.id === id)?.partitionCode || "-";

  /* ================= HISTORY ================= */

  loadMovements = async () => {
    const res = await axios.get(
      process.env.REACT_APP_API + "LiveFishMovement/history",
      { params: { storeId: this.context.storeId } },
    );
    this.setState({ movements: res.data });
  };

  getFilteredMovements = () => {
    const { movements, filterFromDate, filterToDate, filterType } = this.state;

    const from = filterFromDate ? new Date(filterFromDate) : null;
    const to = filterToDate ? new Date(filterToDate) : null;

    if (to) {
      to.setHours(23, 59, 59, 999); // include full end day
    }

    return movements.filter((m) => {
      const mDate = new Date(m.created);

      const dateMatch = (!from || mDate >= from) && (!to || mDate <= to);

      const typeMatch = filterType === "All" || m.movementType === filterType;

      return dateMatch && typeMatch;
    });
  };

  formatDate = (d) => {
    const dt = new Date(d);
    const dd = String(dt.getDate()).padStart(2, "0");
    const mm = String(dt.getMonth() + 1).padStart(2, "0");
    const yy = dt.getFullYear();
    return `${dd}/${mm}/${yy}`;
  };

  printHistory = () => {
    const rows = this.getFilteredMovements();

    const win = window.open("", "", "width=1000,height=800");

    win.document.write(`
    <html>
    <head>
      <title>Live Fish Movement History</title>
      <style>
        body { font-family: Arial; font-size: 12px; padding: 20px; }
        h3 { text-align: center; margin-bottom: 10px; }
        table { width: 100%; border-collapse: collapse; }
        th, td { border: 1px solid #000; padding: 4px; }
        th { background: #f2f2f2; }
      </style>
    </head>
    <body>

      <h3>Live Fish Movement History</h3>
      <div style="text-align:center;margin-bottom:10px;">
        Date: ${this.state.filterFromDate} → ${this.state.filterToDate} |
        Type: ${this.state.filterType}
      </div>

      <table>
        <thead>
          <tr>
            <th>Date</th>
            <th>Product</th>
            <th>From</th>
            <th>To</th>
            <th>Qty</th>
            <th>Type</th>
            <th>Remarks</th>
          </tr>
        </thead>
        <tbody>
          ${rows
            .map(
              (m) => `
              <tr>
                <td>${this.formatDate(m.created)}</td>
                <td>${m.productName}</td>
                <td>${m.fromUnit} → ${m.fromTank} → ${m.fromPartition}</td>
                <td>${
                  m.movementType === "Transfer"
                    ? `${m.toUnit} → ${m.toTank} → ${m.toPartition}`
                    : "-"
                }</td>
                <td>${m.quantity}</td>
                <td>${m.movementType}</td>
                <td>${m.remarks || ""}</td>
              </tr>
            `,
            )
            .join("")}
        </tbody>
      </table>

    </body>
    </html>
  `);

    win.document.close();
    win.focus();
    win.print();
    win.close();
  };

  /* ================= RENDER ================= */

  render() {
    const {
      products,
      units,
      toTanks,
      toPartitions,
      movements,
      form,
      sourceQty,
      error,
      success,
    } = this.state;

    return (
      <div className="container mt-3">
        <h5 className="fw-bold mb-3">Product Movement in Warehouse</h5>

        {error && <Alert variant="danger">{error}</Alert>}
        {success && <Alert variant="success">{success}</Alert>}

        <Card className="mb-4">
          <Card.Body>
            <Row className="mb-2">
              <Col md={6}>
                <Form.Label>Product</Form.Label>
                <Form.Label>Product</Form.Label>
                <Select
                  placeholder="Type fish / unit / tank..."
                  options={this.getProductOptions()}
                  value={this.getProductOptions().find(
                    (o) => o.value === form.productId,
                  )}
                  onChange={(option) => {
                    if (!option) return;

                    const p = option.product;

                    this.setState({
                      selectedProduct: p,
                      sourceQty: p.inStockQty ?? 0,
                      form: {
                        ...this.state.form,
                        productId: p.id,
                        fromUnitId: p.storeUnitId || "",
                        fromTankId: p.tankId || "",
                        fromPartitionId: p.partitionId || "",
                      },
                    });
                  }}
                  filterOption={(option, input) =>
                    option.data.searchText.includes(input.toLowerCase())
                  }
                />
              </Col>

              <Col md={3}>
                <Form.Label>Movement Type</Form.Label>
                <Form.Select
                  name="movementType"
                  value={form.movementType}
                  onChange={this.handleChange}
                >
                  <option value="Transfer">Transfer</option>
                  <option value="Mortality">Mortality</option>
                </Form.Select>
              </Col>

              <Col md={3}>
                <Form.Label>Quantity</Form.Label>
                <Form.Control
                  type="number"
                  name="quantity"
                  value={form.quantity}
                  onChange={this.handleChange}
                />
              </Col>
            </Row>

            {/* SOURCE */}
            <Card className="mb-3 border-secondary">
              <Card.Header className="fw-bold text-muted">
                Source Location
              </Card.Header>
              <Card.Body>
                <Row>
                  <Col md={3}>
                    <Form.Control
                      value={this.getUnitName(form.fromUnitId)}
                      disabled
                    />
                  </Col>
                  <Col md={3}>
                    <Form.Control
                      value={this.getTankName(form.fromTankId)}
                      disabled
                    />
                  </Col>
                  <Col md={3}>
                    <Form.Control
                      value={this.getPartitionName(form.fromPartitionId)}
                      disabled
                    />
                  </Col>
                  <Col md={3}>
                    <Form.Control value={sourceQty} disabled />
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            {/* DESTINATION */}
            {form.movementType === "Transfer" && (
              <Row className="mb-2">
                <Col md={4}>
                  <Form.Select
                    name="toUnitId"
                    value={form.toUnitId}
                    onChange={(e) => {
                      this.handleChange(e);
                      this.loadToTanks(e.target.value);
                    }}
                  >
                    <option value="">-- Select Unit --</option>
                    {units.map((u) => (
                      <option key={u.id} value={u.id}>
                        {u.unitName}
                      </option>
                    ))}
                  </Form.Select>
                </Col>

                <Col md={4}>
                  <Form.Select
                    name="toTankId"
                    value={form.toTankId}
                    onChange={(e) => {
                      this.handleChange(e);
                      this.loadToPartitions(e.target.value);
                    }}
                  >
                    <option value="">-- Select Tank --</option>
                    {toTanks.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.tankCode}
                      </option>
                    ))}
                  </Form.Select>
                </Col>

                <Col md={4}>
                  <Form.Select
                    name="toPartitionId"
                    value={form.toPartitionId}
                    onChange={this.handleChange}
                  >
                    <option value="">-- Select Partition --</option>
                    {toPartitions.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.partitionCode}
                      </option>
                    ))}
                  </Form.Select>
                </Col>
              </Row>
            )}

            <Form.Control
              placeholder="Remarks"
              name="remarks"
              value={form.remarks}
              onChange={this.handleChange}
            />
            <div className="text-end mt-3">
              <Button onClick={this.saveMovement}>Save Movement</Button>
            </div>
          </Card.Body>
        </Card>

        <Card className="mb-2">
          <Card.Body className="p-2">
            <Row className="align-items-end">
              <Col md={3}>
                <Form.Label>From Date</Form.Label>
                <Form.Control
                  type="date"
                  value={this.state.filterFromDate}
                  onChange={(e) =>
                    this.setState({ filterFromDate: e.target.value })
                  }
                />
              </Col>

              <Col md={3}>
                <Form.Label>To Date</Form.Label>
                <Form.Control
                  type="date"
                  value={this.state.filterToDate}
                  onChange={(e) =>
                    this.setState({ filterToDate: e.target.value })
                  }
                />
              </Col>

              <Col md={3}>
                <Form.Label>Movement Type</Form.Label>
                <Form.Select
                  value={this.state.filterType}
                  onChange={(e) =>
                    this.setState({ filterType: e.target.value })
                  }
                >
                  <option value="All">All</option>
                  <option value="Transfer">Transfer</option>
                  <option value="Mortality">Mortality</option>
                </Form.Select>
              </Col>

              <Col md={3}>
                <Button variant="outline-secondary" onClick={this.printHistory}>
                  🖨️ Print History
                </Button>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* HISTORY */}
        <Table bordered size="sm">
          <thead>
            <tr>
              <th>Date</th>
              <th>Product</th>
              <th>From</th>
              <th>To</th>
              <th>Qty</th>
              <th>Type</th>
              <th>Remarks</th>
            </tr>
          </thead>
          <tbody>
            {this.getFilteredMovements().map((m) => (
              <tr key={m.id}>
                <td>{this.formatDate(m.created)}</td>
                <td>{m.productName}</td>
                <td>{`${m.fromUnit} → ${m.fromTank} → ${m.fromPartition}`}</td>
                <td>
                  {m.movementType === "Transfer"
                    ? `${m.toUnit} → ${m.toTank} → ${m.toPartition}`
                    : "-"}
                </td>
                <td>{m.quantity}</td>
                <td>{m.movementType}</td>
                <td>{m.remarks}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
    );
  }
}
